import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Button, Icon, IconButton, Skeleton, useDisclosure } from '@chakra-ui/react';
import { Box, Flex, Text, Stack } from '@chakra-ui/layout';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import SharedExperienceIcon from '../../../components/icons/sharedExperienceIcon';
import { supabase } from '../../../supabase.client';
import { useNavigate } from 'react-router-dom';
import SharedExperienceForm from '../../../shared-experiences/components/sharedExperienceForm';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal';
import AddSharedExperienceToSharedExperienceModalForm from './AddSharedExperienceToSharedExperienceModalForm';
import { SharedExperiencesContext } from '../../lib/sharedExperiencesProvider';
import { SharedExperiencePageContext } from '../lib/sharedExperiencePageContext';
import { useSharedExperiencesApi } from '../../../shared-experiences/lib/useSharedExperiencesApi';
import useTranslation from '../../../lib/i18n/useTranslation';

export default function SharedExperiencesInSharedExperienceList({ sharedExperienceId }) {
  const { t } = useTranslation();
  const { rights } = useContext(SharedExperiencesContext);
  const {
    isFetchingSharedExperiences,
    sharedExperienceList,
    refreshSharedExperienceList,
    refreshExperienceList,
    addingSharedExperience,
    setAddingSharedExperience,
  } = useContext(SharedExperiencePageContext);
  const [isAdding, setIsAdding] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const modalAddSharedExperience = useDisclosure();
  const modalCreateSharedExperience = useDisclosure();
  const ref = useRef(null);
  const ref1 = useRef(null);
  const navigate = useNavigate();
  const sharedExperiencesApi = useSharedExperiencesApi();

  useEffect(() => {
    if (addingSharedExperience) {
      modalAddSharedExperience.onOpen();
    }
  }, [addingSharedExperience]);

  const handleCloseAddModal = () => {
    modalAddSharedExperience.onClose();
    setAddingSharedExperience(false);
  };

  const removeOneSharedExperienceFromSharedExperience = async (linkedSharedExperienceId, sharedExperienceId) => {
    try {
      const { error } = await supabase.rpc('remove_shared_experience_from_shared_experience', {
        arg_parent_shared_experiences_id: sharedExperienceId,
        arg_child_shared_experiences_id: linkedSharedExperienceId
      });
      
      if (error) {
        console.error('Error removing shared experience from shared experience:', error);
        throw error;
      }
      
      await refreshSharedExperienceList();
      await refreshExperienceList();
      
    } catch (error) {
      console.error('Error removing shared experience from shared experience:', error);
    }
  };
  return (
    <>
      <Flex 
        w="100%"
        flexDirection={"column"}
      >
        <Flex 
          alignItems='center'
          justifyContent={"space-between"}
          px={3}
          mb={2}
        >
          <Flex
            alignItems='center'
          >
            <Icon
              as={SharedExperienceIcon}
              boxSize={'14px'}
              mr={2}
            />
            <Text fontSize={14}>{t('sharedExperience.sharedExperiences')}</Text>
          </Flex>
          {rights?.can_update && (
            <IconButton
              ml={2}
              variant="greenOutline"
              icon={<MdAdd />}
              size="sm"
              onClick={modalAddSharedExperience.onOpen}
            />
          )}
        </Flex>
        <Box maxH={300} overflowY={'auto'}>
          {isFetchingSharedExperiences && (
            <Stack p={3} spacing={2} backgroundColor={'white'}>
              <Skeleton height='30px' />
              <Skeleton height='30px' />
              <Skeleton height='30px' />
            </Stack>
          )}
          {(!isFetchingSharedExperiences && (!sharedExperienceList || sharedExperienceList.length === 0)) && (
            <Box px={3}>
              <Text color="text.light.grey">{t('sharedExperience.noSharedExperiences')}</Text>
            </Box>
          )}
          {(!isFetchingSharedExperiences && sharedExperienceList && sharedExperienceList.length > 0) && (
            <Stack spacing={1}>
              {sharedExperienceList
                .filter((link, index, self) => 
                  self.findIndex(l => l.child_shared_experiences_id === link.child_shared_experiences_id) === index
                )
                .map((link) => (
                <Flex
                  key={link.id}
                  px={3}
                  h={"34px"}
                  backgroundColor="white"
                  role={'group'}
                  _hover={{
                    background: "background.grey",
                    borderRadius: '2px'
                  }}
                  position={"relative"}
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                  cursor="pointer"
                  onClick={() => {
                    navigate(`/shared-experiences/${link.child_shared_experiences_id}`);
                  }}
                >
                  <Flex alignItems="center" flex={1}>
                    <Text fontSize="sm" noOfLines={1}>
                      {link.child_shared_experience?.name}
                    </Text>
                  </Flex>
                  <Box style={{ opacity: '0' }} _groupHover={{ opacity: '1 !important' }} >
                    <IconButton
                      icon={<Icon as={MdAdd} transform="rotate(45deg)" />}
                      size={'md'}
                      variant='greyTransparent'
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removeOneSharedExperienceFromSharedExperience(link.child_shared_experiences_id, sharedExperienceId);
                      }}
                    />
                  </Box>
                </Flex>
              ))}
            </Stack>
          )}
        </Box>
      </Flex>
      <Modal isOpen={modalAddSharedExperience.isOpen} onClose={handleCloseAddModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('sharedExperience.addSharedExperience')}</ModalHeader>
          <ModalBody>
            <Box p={3}>
              <AddSharedExperienceToSharedExperienceModalForm
                sharedExperienceId={sharedExperienceId}
                hideSave={true}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={async () => {
                  setIsAdding(false);
                  handleCloseAddModal();
                  await refreshSharedExperienceList();
                  await refreshExperienceList();
                }}
                onCreateNew={async (name) => {
                  modalAddSharedExperience.onClose();
                  modalCreateSharedExperience.onOpen();
                }}
                onValidityChange={setIsValid}
                ref={ref}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='greenOutline'
              onClick={handleCloseAddModal}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={isAdding}
              isDisabled={!isValid}
              onClick={() => {
                ref.current.click();
              }}
            >
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={modalCreateSharedExperience.isOpen} onClose={modalCreateSharedExperience.onClose}>
        <ModalOverlay />
        <ModalContent >
          <ModalHeader>
            <IconButton
              fontSize='24'
              variant="tertiary"
              icon={<MdArrowBack />}
              aria-label='Back'
              mr={2}
              onClick={() => {
                modalCreateSharedExperience.onClose();
                modalAddSharedExperience.onOpen();
              }}
            />
            {t('sharedExperience.newSharedExperience')}</ModalHeader>
          <ModalBody >
            <Box p={3}>
              <SharedExperienceForm
                hideSave={true}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={async (newSharedExperience) => {
                  setIsAdding(false);
                  if (newSharedExperience && newSharedExperience.id) {
                    try {
                      await sharedExperiencesApi.linkSharedExperienceToSharedExperience(newSharedExperience.id, sharedExperienceId);
                      await refreshSharedExperienceList();
                      await refreshExperienceList();
                    } catch (error) {
                      console.error('Error linking new shared experience:', error);
                    }
                    modalCreateSharedExperience.onClose();
                  }
                }}
                onValidityChange={setIsValid}
                ref={ref1}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='greenOutline'
              onClick={() => {
                modalCreateSharedExperience.onClose();
                modalAddSharedExperience.onOpen();
              }}
            >Cancel</Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={isAdding}
              isDisabled={!isValid}
              onClick={() => {
                ref1.current.click();
              }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
} 